import React, { useState, useRef, useEffect } from 'react';
import Icon from './AppIcon';

const roomTypeCategories = [
  {
    category: 'Core Living Spaces',
    options: ['Living Room', 'Dining Room', 'Bedroom', "Kids' Room", 'Guest Room'],
  },
  {
    category: 'Kitchen & Utility',
    options: ['Kitchen', 'Open Kitchen-Dining', 'Pantry', 'Laundry Room'],
  },
  {
    category: 'Bathrooms & Wellness',
    options: ['Bathroom', 'Powder Room', 'Walk-In Closet', 'Sauna / Spa Room'],
  },
  {
    category: 'Work & Study',
    options: ['Home Office', 'Study / Library', 'Studio / Creative Room'],
  },
  {
    category: 'Transitional & Circulation Spaces',
    options: ['Entryway / Foyer', 'Hallway / Corridor', 'Staircase Area'],
  },
  {
    category: 'Leisure & Entertainment',
    options: ['Home Theater / Media Room', 'Game Room / Playroom', 'Home Bar / Lounge', 'Home Gym'],
  },
  {
    category: 'Outdoor-Adjacent & Semi-Open',
    options: ['Balcony', 'Terrace / Patio', 'Sunroom / Conservatory', 'Veranda / Covered Porch'],
  },
  {
    category: 'Commercial / Hospitality',
    options: ['Hotel Room', 'Restaurant', 'Café / Coffee Shop', 'Bar / Nightclub', 'Reception / Lobby', 'Meeting Room / Conference Room', 'Co-Working Space', 'Retail Store / Showroom', 'Salon / Beauty Studio'],
  },
];

const RoomTypeDropdown = ({
  value = '--',
  onChange,
  label = 'Room type',
  required = false,
  mobile = false,
  dropDirection = 'up',
  extraTopOptions = [],
  showCustomOption = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setExpandedCategory(null);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setExpandedCategory(null);
  };

  return (
    <div ref={ref}>
      <label className={`text-white/70 block ${mobile ? 'text-xs mb-1' : 'text-xs mb-2'}`}>
        {label}
      </label>
      <div className="relative">
        <button
          onClick={() => { setIsOpen(!isOpen); if (isOpen) setExpandedCategory(null); }}
          className={`w-full glassmorphic-select bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all ${
            mobile ? 'rounded-xl px-3 h-[38px]' : 'rounded-2xl px-4 py-3'
          }`}
        >
          <div className={`flex items-center ${mobile ? 'gap-2' : 'gap-3'}`}>
            <Icon name="Home" size={mobile ? 14 : 18} className={value !== '--' ? 'text-orange-500' : 'text-white/50'} />
            <span className={`text-white truncate ${mobile ? 'text-xs' : 'text-sm'}`}>{value}</span>
          </div>
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={mobile ? 14 : 16} className="text-white/50" />
        </button>

        {isOpen && (
          <div
            className={`absolute z-50 w-full border border-white/10 shadow-2xl overflow-hidden overflow-y-auto ${
              dropDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
            } ${mobile ? 'rounded-xl max-h-[350px]' : 'rounded-2xl max-h-[400px]'}`}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
          >
            {/* Default option */}
            <button
              onClick={() => handleSelect('--')}
              className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                value === '--' ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
              }`}
            >
              <span className={`text-sm ${value === '--' ? 'text-white font-medium' : 'text-white/70'}`}>--</span>
            </button>

            {/* Extra top options */}
            {extraTopOptions.map((option) => (
              <button
                key={option}
                onClick={() => handleSelect(option)}
                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                  value === option ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                }`}
              >
                <span className={`text-sm ${value === option ? 'text-white font-medium' : 'text-white/70'}`}>{option}</span>
              </button>
            ))}

            {roomTypeCategories.map((cat) => {
              const isCategoryActive = cat.options.includes(value);
              return (
                <div key={cat.category}>
                  {/* Category header */}
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === cat.category ? null : cat.category)}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                      isCategoryActive ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                    }`}
                  >
                    <span className={`text-sm ${isCategoryActive ? 'text-white font-medium' : 'text-white/70'}`}>{cat.category}</span>
                    <Icon name={expandedCategory === cat.category ? 'ChevronUp' : 'ChevronDown'} size={14} className={isCategoryActive ? 'text-orange-400' : 'text-white/50'} />
                  </button>

                  {/* Category options */}
                  {expandedCategory === cat.category && cat.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleSelect(option)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 pl-8 transition-all border-b border-white/5 last:border-b-0 ${
                        value === option
                          ? 'bg-orange-500/20 hover:bg-orange-500/30 text-white font-medium'
                          : 'hover:bg-white/5 text-white/70'
                      }`}
                    >
                      <span className={`text-xs ${value === option ? 'text-orange-400' : 'text-orange-500/50'}`}>•</span>
                      <span className="text-sm">{option}</span>
                    </button>
                  ))}
                </div>
              );
            })}

            {/* Custom option — shown only when showCustomOption=true */}
            {showCustomOption && (
              <button
                onClick={() => handleSelect('Custom')}
                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-t border-white/10 ${
                  value === 'Custom' ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="PenLine" size={14} className={value === 'Custom' ? 'text-orange-400' : 'text-white/50'} />
                  <span className={`text-sm ${value === 'Custom' ? 'text-white font-medium' : 'text-white/70'}`}>Custom</span>
                </div>
                <span className="text-[10px] text-white/30 italic">type in prompt</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomTypeDropdown;
